import React, { useState, useEffect } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import UserApi from "../../services/users";
import DashboardHeader from '../../components/DashboardHeader';

const normalizeStatusValue = (value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['true', '1', 'active', 'yes', 'enabled'].includes(normalized)) return true;
        if (['false', '0', 'inactive', 'no', 'disabled'].includes(normalized)) return false;
    }
    return false;
};

const deriveStatusFromData = (data) => {
    if (!data) return false;
    if (Object.prototype.hasOwnProperty.call(data, 'status')) {
        return normalizeStatusValue(data.status);
    }
    if (Object.prototype.hasOwnProperty.call(data, 'is_active')) {
        return normalizeStatusValue(data.is_active);
    }
    return false;
};

function EditUserPage(props) {
    const { userId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role_id: '',
        status: true,
        is_superuser: false
    });
    const [networkRoles, setNetworkRoles] = useState({}); // Store roles per network
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tenantId] = useState('1'); // Default tenant ID
    const [user, setUser] = useState({});

    // Get filter state from navigation
    const filters = location.state?.filters || {};

    const getBackUrl = () => {
        let url = '/users';
        const params = new URLSearchParams();
        
        if (filters.search) params.append('search', filters.search);
        if (filters.selectedNetwork) params.append('network', filters.selectedNetwork);
        if (filters.activeLetter && filters.activeLetter !== 'all') params.append('letter', filters.activeLetter);
        
        const queryString = params.toString();
        return queryString ? `${url}?${queryString}` : url;
    };

    useEffect(() => {
        const fetchUserAndRoles = async () => {
            const userApi = new UserApi(props.token);
            let userData = null;
            
            // Get user data from navigation state or try to fetch
            if (location.state && location.state.user) {
                userData = location.state.user;
                const statusValue = deriveStatusFromData(userData);
                setUser({
                    ...userData,
                    status: statusValue
                });
                    setFormData({
                        first_name: userData.first_name || '',
                        last_name: userData.last_name || '',
                        email: userData.email || '',
                        phone: userData.phone || '',
                        role_id: userData.role?.id || '',
                    status: statusValue,
                        is_superuser: userData.is_superuser || false
                    });
                
                // Initialize network roles
                if (userData.networks && userData.networks.length > 0) {
                    const initialNetworkRoles = {};
                    userData.networks.forEach(network => {
                        initialNetworkRoles[network.tenant_id] = network.role_id || '';
                    });
                    setNetworkRoles(initialNetworkRoles);
                }
            } else {
                // Try to fetch user data directly
                try {
                    const foundUser = await userApi.getById(userId);
                    userData = foundUser;
                    const statusValue = deriveStatusFromData(foundUser);
                    setUser({
                        ...foundUser,
                        status: statusValue
                    });
                    setFormData({
                        first_name: foundUser.first_name || '',
                        last_name: foundUser.last_name || '',
                        email: foundUser.email || '',
                        phone: foundUser.phone || '',
                        role_id: foundUser.role?.id || '',
                        status: statusValue,
                        is_superuser: foundUser.is_superuser || false
                    });
                    
                    // Initialize network roles
                    if (foundUser.networks && foundUser.networks.length > 0) {
                        const initialNetworkRoles = {};
                        foundUser.networks.forEach(network => {
                            initialNetworkRoles[network.tenant_id] = network.role_id || '';
                        });
                        setNetworkRoles(initialNetworkRoles);
                    }
                } catch (error) {
                    console.error('Error fetching user:', error);
                    navigate('/users');
                    return;
                }
            }

            // Fetch roles for each network
            if (userData && userData.networks && userData.networks.length > 0) {
                try {
                    // Use the first network's tenant ID for roles (assuming roles are consistent across networks)
                    const firstNetworkTenantId = userData.networks[0].tenant_id;
                    const response = await userApi.getRoles(firstNetworkTenantId);
                    const filteredRoles = response.filter(role => 
                        role.name === 'Admin' || role.name === 'Owner' || role.name === 'Member'
                    );
                    setRoles(filteredRoles);
                } catch (error) {
                    console.error('Error fetching roles:', error);
                }
            } else {
                // Fallback to default tenant ID if no networks
                try {
                    const response = await userApi.getRoles(tenantId);
                    const filteredRoles = response.filter(role => 
                        role.name === 'Admin' || role.name === 'Owner' || role.name === 'Member'
                    );
                    setRoles(filteredRoles);
                } catch (error) {
                    console.error('Error fetching roles:', error);
                }
            }
        };

        if (userId) {
            fetchUserAndRoles();
        }
    }, [userId, props.token, tenantId, navigate, location.state]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;

        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));
    };

    const handleNetworkRoleChange = (tenantId, roleId) => {
        setNetworkRoles(prev => ({
            ...prev,
            [tenantId]: roleId
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const userApi = new UserApi(props.token);
            
            // Convert status to boolean (checkbox is already boolean but ensure)
            const isActive = !!formData.status;
            
            // Try to update user basic information via the general users endpoint
            try {
                await userApi.updateUser(userId, {
                    first_name: formData.first_name,
                    last_name: formData.last_name,
                    email: formData.email,
                    phone: formData.phone,
                    is_active: isActive,
                    is_superadmin: formData.is_superuser  // Map is_superuser to is_superadmin for API
                });
            } catch (updateError) {
                console.warn('User update endpoint not available:', updateError.message);
                // Continue to update network roles even if user update fails
            }

            // Update roles for each network (this endpoint definitely works)
            if (user.networks && user.networks.length > 0) {
                for (const network of user.networks) {
                    const newRoleId = networkRoles[network.tenant_id];
                    if (newRoleId && newRoleId !== network.role_id) {
                        console.log(`Updating role for network ${network.tenant_id} to role ${newRoleId}`);
                        await userApi.updateMemberRole(network.tenant_id, userId, newRoleId);
                    }
                }
            }
            
            // Navigate back to users with preserved filters
            navigate(getBackUrl());
        } catch (error) {
            console.error('Error updating user:', error);
            alert('Error updating user: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className='dashboard-content'>
            <DashboardHeader
                btnText="Back to Users" 
                link={getBackUrl()} />
            <div className='dashboard-content-container'>
                <div className='dashboard-content-header'>
                    <h2>Edit User: {user.first_name} {user.last_name}</h2>
                </div>
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label htmlFor="first_name" className="form-label">First Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="first_name"
                                            name="first_name"
                                            value={formData.first_name}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label htmlFor="last_name" className="form-label">Last Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="last_name"
                                            name="last_name"
                                            value={formData.last_name}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label htmlFor="email" className="form-label">Email</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            id="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label htmlFor="phone" className="form-label">Phone</label>
                                        <input
                                            type="tel"
                                            className="form-control"
                                            id="phone"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-3">
                                            <div className="form-check form-switch">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="status"
                                                    name="status"
                                                    checked={!!formData.status}
                                                    onChange={handleInputChange}
                                                />
                                                <label className="form-check-label" htmlFor="status">
                                                    {formData.status ? 'Active' : 'Inactive'}
                                                </label>
                                            </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Additional User Info */}
                            <div className="row mt-3">
                                <div className="col-12">
                                    <h5>Additional Information</h5>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="is_superuser" className="form-label">Superuser</label>
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id="is_superuser"
                                                        name="is_superuser"
                                                        checked={formData.is_superuser}
                                                        onChange={handleInputChange}
                                                    />
                                                    <label className="form-check-label" htmlFor="is_superuser">
                                                        Yes
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Network Memberships Section - Only show if user has networks */}
                            {user.networks && user.networks.length > 0 && (
                                <div className="row mt-3">
                                    <div className="col-12">
                                        <h5>Network Memberships</h5>
                                        
                                        {/* Network Information with Role Selection */}
                                        {user.networks.map((network, index) => (
                                            <div key={index} className="card mb-3">
                                                <div className="card-body">
                                                    <div className="row">
                                                        <div className="col-md-6">
                                                            <p><strong>Network:</strong> {network.tenant_name}</p>
                                                            <div className="mb-3">
                                                                <label htmlFor={`role_id_${index}`} className="form-label">Network Role</label>
                                                                <select
                                                                    className="form-control"
                                                                    id={`role_id_${index}`}
                                                                    name={`role_${network.tenant_id}`}
                                                                    value={networkRoles[network.tenant_id] || network.role_id || ''}
                                                                    onChange={(e) => handleNetworkRoleChange(network.tenant_id, e.target.value)}
                                                                    required
                                                                >
                                                                    <option value="">Select a role</option>
                                                                    {roles.map(role => (
                                                                        <option key={role.id} value={role.id}>
                                                                            {role.name}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <p><strong>Current Network Role:</strong> {network.role_name}</p>
                                                            <p><strong>Membership Status:</strong> 
                                                                <span className={`badge ms-2 ${
                                                                    network.status === 'approved' ? 'bg-success' : 
                                                                    network.status === 'pending' ? 'bg-warning' : 
                                                                    network.status === 'rejected' ? 'bg-danger' : 'bg-secondary'
                                                                }`}>
                                                                    {network.status}
                                                                </span>
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            <div className="row">
                                <div className="col-12">
                                    <button 
                                        type="submit" 
                                        className="btn btn-primary"
                                        disabled={loading}
                                    >
                                        {loading ? 'Updating...' : 'Update User'}
                                    </button>
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary ms-2"
                                        onClick={() => navigate('/users')}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default EditUserPage
